// Baker-2 static ways (APP_BAKER §4 "The ground under a way (WAYS-G)"). WAYS-G (waysg CONTRACT C1 / C2):
// every surface way — road, rail, track, path, STAIRS — is drawn BY THE
// GROUND (game/waysdraw.js isGroundWay; drawnRibbons(doc, { groundWays:
// true }) tags every piece `ground: 1`; the terrain shader draws them from
// the bake's ways field, client/src/groundmat.js): no strip, no float, no
// z-fight. What is still meshed — by the BAKE since R0 WAY1 (the waytiles
// step runs client/src/waybuild.js: game/waysdraw.js, the same geometry the
// gates tap, merged per 512 m chunk (RIBBON_CHUNK) and per (view class,
// material) into the way1/<tx>_<tz>.bin tiles, game/way1.js; uploaded here as
// they are, per-class view ranges (ribbonVisible)): a WAY's ribbon on its DECK (client/src/deckway.js), the ring
// island kerb tops (paintParts kerbs) and the rail heads + frogs (forkDraw
// head lines, instanced railUnit) — never a funicular's (AMENDMENT 1 A4: the funicular
// is W's open steel structure). The fork beds (forkDraw strips / trimmed
// triangles) are ground like every other rail piece.
// Bore stations are NOT drawn here (bores and decks: structures.bin, drawn by
// structs2.js, S6.a / S6.b); a way's deck stations are (BRIDGE P1, owner
// 2026-10-16: "the train tracks should look the same on the bridge as off
// the bridge, same for roads" — client/src/deckway.js deckWays: every deck
// whose top is not timber / stairs / a funicular's, its ribbon on the deck
// top joined to the approaches, drawn in the GROUND's look of its way kind by
// deckwaymat.js; a rail deck's railUnit rails (waybuild.js accumulate, structplan
// railRuns deck: 'only') and 3D sleepers (nearDetail on the deck pieces) as
// the ground's). The bore and deck terrain carves register here;
//
// The paint (S7 DRAW-1, owner O3 / O4) — since WAYS-G only on the deck
// ribbons (the ground draws every other way's sidewalk / apron / lines from
// the field): game/waysdraw.js paintParts reads
// the sidewalk strips (e.sw), the ring apron and lanes (e.ring, doc.rings)
// into per-vertex u bounds; a chunk geometry carrying any of them gets two
// u8 attributes (aPart, aWay) and the class material's PAINT twin (one
// shader patch of the map lookup: sidewalk slabs + kerb line, apron setts,
// the carriageway texture squeezed into its own width). Same mesh, same draw,
// same triangles; the only new triangles are the island kerb tops
// (paintParts kerbs), merged into the ring's own chunk / material. A chunk
// without paint keeps the stock material.
//
// The lane lines (MARKS: game/waysdraw.js laneMarks): the ribbon textures
// are plain; per vertex aLane = [lanes against (+ 64: the centre line is
// SOLID, laneLayout solid / divider=line), lanes with the run] and per
// quad aCut (a FLAT attribute on the quad's provoking vertex, section q's
// lo vertex for quad (q-1, q)) = the arc interval its lines are hidden in
// (another ribbon covers them: forks, crossings, merges). The shader draws a
// dashed centre line between the two directions and dashed dividers between
// lanes of one direction, in metres, antialiased by the pixel's footprint.
//
// The rail junctions (S7 D-2, owner O9): game/waysdraw.js forkDraw (pure,
// three-free) redraws every rail piece from ways.json forks[]: a turnout's
// blades from the node and its frog casting at frog_s, one ballast bed per
// group (the diverging windows and the neighbouring tracks trimmed, no bed
// overlaps another), a facing gap's two dead ends joined (a lateral pair
// left apart), run ends meeting on one section, every track at its recorded
// gauge (railGauges: the one resolution structplan railRuns reads too, so a
// deck / bore end meets these rails at one gauge; a turnout on a deck is
// structplan's). Its output is meshed by the bake (waybuild.js): strips + trimmed triangles
// into the same chunk / material accumulators, head-line segments + frogs as
// instances of one railUnit (L-h). The head lines carry their gauge in their
// points, so one instanced steel draw per chunk holds every gauge (rail and funicular share the 'rail'
// material). A world older than PAINT.world_rev / FORK.world_rev: the bake refuses it.
//
// The drawn pieces' outlines ride in the tiles (1 cm): `pieces` (objects.js
// cutWells), the road mask, flightAt and wayLabelAt (their flight flag and
// surface label baked in). ways.json is the server's and the gates' (private,
// game/protocol/public.json); the readers below read ways_slim.json
// (game/way1.js SLIM).
//
// Also the source of the bike RoadGraph (surface runs), the road mask (every
// piece rastered at 2 m/px, _mask -> at(): grass / tree / shrub exclusion,
// bike, net; the drawn-strip plane and its terrain texture went with the
// strips, WAYS-G) and the TRAIL INDEX (TRAILS-C, client/src/trailmarks.js
// buildTrailIndex: the path-family runs with their grade / mark, decimated
// xz polylines, length + climb; graded runs keep their stations until
// TrailMarks has placed the marker posts): `trails`, `trailByRun`,
// `trailAt(x, y, z)` — the map's trail layer, the HUD chip, inspect.
import * as THREE from 'three';
import { ribbonVisible } from '../../game/lodmath.js';
import { classDraw } from './device.js';
import { PAINT, MARKS, DETAIL } from '../../game/waysdraw.js';
import WAY1 from '../../game/way1.js';
import { platformsIndex, platformAt, platformLabel } from '../../game/pave.js';
import WAYSLOOK from '../../pipeline/wayslook.json';
import RENDER_CFG from './renderconfig.json';
import { fetchAsset } from './assetq.js';
import { decodeWays, runSteps } from './decode.js';
import { slice, act } from './sched.js';
import { maskFill } from './maskfill.js';
import { ribbonUniforms } from './wire.js';
import { makeMaterials } from './roads3d.js';
import { deckWayMaterial } from './deckwaymat.js';
import { RoadGraph } from './roadgraph.js';
import { addBoreRuns } from './boreruns.js';
import { buildTrailIndex, trailNear } from './trailmarks.js';

const CHUNK = WAY1.TILE_M;    // the tile lattice = RIBBON_CHUNK
// the class's upload cell (quest-merge 2026-11-01): CHUNK × CLASS_DRAW way_cell
// (device.js; quest 2 = 1024 m): the tiles of a cell share one chunk group and
// their meshes of one (material, view class) are MERGED into one draw (the
// rails' instances concatenated); the per-class view rule (update) measures
// from the cell's centre. Every other class: the tile itself, as uploaded.
export const wayCellM = (cls) => CHUNK * classDraw('way_cell', cls);
// a + b (same attribute set: names, item sizes, array types, normalisation),
// one indexed geometry; null when they differ (the caller keeps b apart)
export function mergeGeometries(a, b) {
  const names = Object.keys(a.attributes);
  if (names.length !== Object.keys(b.attributes).length || !a.index || !b.index) return null;
  const out = new THREE.BufferGeometry();
  for (const n of names) {
    const p = a.attributes[n], q = b.attributes[n];
    if (!q || p.itemSize !== q.itemSize || p.array.constructor !== q.array.constructor || p.normalized !== q.normalized) return null;
    const arr = new p.array.constructor(p.array.length + q.array.length);
    arr.set(p.array, 0); arr.set(q.array, p.array.length);
    out.setAttribute(n, new THREE.BufferAttribute(arr, p.itemSize, p.normalized));
  }
  const na = a.attributes.position.count, ia = a.index.array, ib = b.index.array;
  const ix = new (na + b.attributes.position.count > 65535 ? Uint32Array : Uint16Array)(ia.length + ib.length);
  ix.set(ia, 0);
  for (let i = 0; i < ib.length; i++) ix[ia.length + i] = ib[i] + na;
  out.setIndex(new THREE.BufferAttribute(ix, 1));
  out.computeBoundingSphere();
  return out;
}
// rail head over the ballast (roads3d: LIFT.rail + 0.13 over its bed) and the
// rail section: game/way1.js, the bake's instance matrices use the same
const { RAIL_LIFT, RAIL_PROFILE } = WAY1;
const MASK_CELL = 2;          // road mask m/px
// rail steel (L-h): the running top polished, the sides weathered to rust
// at the foot (a vertex colour across the head, x the material's white)
const RAIL_TOP = new THREE.Color(0xa9aeb3), RAIL_FOOT = new THREE.Color(0x5b4637);
// near 3D detail (renderconfig.json `ways`): sleepers / kerb faces within
// their view ranges, one instanced draw, re-selected every detail_step_m
const WCFG = RENDER_CFG.ways;
if (!WCFG || !(WCFG.sleeper_view_m > 0) || !(WCFG.kerb_view_m > 0) || !(WCFG.detail_step_m > 0) || !(WCFG.detail_cap > 0)) {
  throw new Error('renderconfig.json ways {sleeper_view_m, kerb_view_m, detail_step_m, detail_cap} missing');
}
const lin = (c) => new THREE.Color().setRGB(c[0] / 255, c[1] / 255, c[2] / 255, THREE.SRGBColorSpace);
const SLEEPER_C = lin(WAYSLOOK.ballast.colour_sleeper), KERB_C = lin(WAYSLOOK.kerb.colour);
// a per-stone weathering: brightness x [1 - a, 1] from a hash of the
// position (deterministic: the same stone, the same shade on every load)
const jit = (x, z, a) => {
  const h = Math.sin(Math.round(x * 10) * 12.9898 + Math.round(z * 10) * 78.233) * 43758.5453;
  return 1 - a * (h - Math.floor(h));
};

// the paint's map lookup (replaces three's map_fragment; USE_MAP is always on
// for the ribbon classes). u = vMapUv.x across the formation, metres along =
// vMapUv.y x texLen. The texture is sampled ONCE and the line footprint taken
// outside the branches (the derivatives stay uniform); identity bounds
// [0, 0, 1, 1] sample at u exactly as the stock material does.
const PAINT_GLSL = `
#ifdef USE_MAP
  float pu = vMapUv.x, pw = vWay.x, pm = vMapUv.y * vWay.y;
  float cu0 = vPart.y, cu1 = vPart.z;
  float uc = clamp((pu - cu0) / max(cu1 - cu0, 1e-4), 0.0, 1.0);
  float pfw = max(fwidth(pu) * pw, 1e-4);
  vec4 pcol = texture2D(map, vec2(uc, vMapUv.y));
  float sw = pu < vPart.x ? vPart.x - pu : (pu > vPart.w ? pu - vPart.w : -1.0);
  if (vWay.w > 0.5) {
    pcol = vec4(uPaintKerb, 1.0);
  } else if (sw >= 0.0) {
    float om = (pu < vPart.x ? pu : 1.0 - pu) * pw;
    vec3 c = uPaintSidewalk;
    float jf = uPaintA.y / uPaintA.x;
    if (fract(pm / uPaintA.x) < jf || fract(om / uPaintA.x) < jf) c = uPaintJoint;
    if (sw * pw < uPaintA.z) c = uPaintKerb;
    pcol = vec4(c, 1.0);
  } else if (pu < cu0 || pu > cu1) {
    float om = (pu < cu0 ? cu0 - pu : pu - cu1) * pw;
    float row = floor(om / uPaintA.w);
    vec3 c = uPaintApron;
    if (fract(om / uPaintA.w) < uPaintC.w || fract(pm / uPaintA.w + 0.5 * row) < uPaintC.w) c = uPaintSett;
    pcol = vec4(c, 1.0);
  } else if (mod(vLane.x, 64.0) + vLane.y > 1.5 && !(pm >= vCut.x && pm <= vCut.y)) {
    // (SAN-GIAN) aLane.x = lanes against + 64 when the centre line is solid
    float sol = step(63.5, vLane.x), lb = vLane.x - 64.0 * sol;
    float n = lb + vLane.y, cw = (cu1 - cu0) * pw, om = uc * cw, lane = cw / n;
    float kr = floor(om / lane + 0.5), lw = uPaintB.x;
    if (kr >= 1.0 && kr <= n - 1.0) {
      float d = abs(om - kr * lane);
      float cov = clamp(min(d + 0.5 * pfw, 0.5 * lw) - max(d - 0.5 * pfw, -0.5 * lw), 0.0, pfw) / pfw;
      bool ctr = abs(kr - lb) < 0.5 && lb > 0.5 && vLane.y > 0.5;
      vec2 dg = ctr ? uMarkA.xy : uMarkA.zw;
      float on = ctr && sol > 0.5 ? 1.0 : step(fract(pm / (dg.x + dg.y)), dg.x / (dg.x + dg.y));
      pcol.rgb = mix(pcol.rgb, uPaintLine, cov * on);
    }
  }
  diffuseColor *= pcol;
#endif
`;

// L-h: forkDraw's rails instanced — one rail segment from x 0 to 1, z
// across, the running top at y 0: a flat-shaded rail section (RAIL_PROFILE:
// head, web, the foot flanges standing on the sleeper top DETAIL.top_m over
// the ballast; head / web / foot tops, 14 triangles; vertex colours: the
// polished running top, weathered sides). An instance: x = the segment of a
// head line (B - A, slope in), y up, z the horizontal normal, at A + lift —
// every head line (plain, a fork's blade / outer rail); a frog casting = the
// same section f.len long, z scaled so its head is f.w wide, FORK.frog_lift_m
// over the heads. FORK.rail_w / rail_h stay forkDraw's spacing, not the look.
function railUnit() {
  const P = RAIL_PROFILE, hw = P.head_w / 2, ww = P.web_w / 2, fw = P.foot_w / 2;
  const yB = -(RAIL_LIFT - DETAIL.top_m), yH = -P.head_h, yF = yB + P.foot_h;
  const pos = [], nrm = [], col = [], idx = [];
  const C = (t) => (t ? RAIL_TOP : RAIL_FOOT);
  // a quad across x 0..1 between (y0, z0) and (y1, z1), normal n, colours c0 / c1
  const quad = (y0, z0, y1, z1, n, c0, c1) => {
    const k = pos.length / 3;
    pos.push(0, y0, z0, 1, y0, z0, 1, y1, z1, 0, y1, z1);
    for (let i = 0; i < 4; i++) nrm.push(0, n[0], n[1]);
    for (const c of [c0, c0, c1, c1]) col.push(c.r, c.g, c.b);
    // wind so the face looks along n: (v1 - v0) x (v2 - v0) = (0, z0 - z1, y1 - y0)
    if ((z0 - z1) * n[0] + (y1 - y0) * n[1] > 0) idx.push(k, k + 1, k + 2, k, k + 2, k + 3);
    else idx.push(k, k + 2, k + 1, k, k + 3, k + 2);
  };
  const top = C(1), side = new THREE.Color().copy(RAIL_TOP).lerp(RAIL_FOOT, 0.55), rust = C(0);
  quad(0, -hw, 0, hw, [1, 0], top, top);                 // running top
  quad(0, hw, yH, hw, [0, 1], side, rust);               // head sides
  quad(0, -hw, yH, -hw, [0, -1], side, rust);
  quad(yH, ww, yF, ww, [0, 1], rust, rust);              // web
  quad(yH, -ww, yF, -ww, [0, -1], rust, rust);
  quad(yF, ww, yF, fw, [1, 0], rust, rust);              // foot flanges
  quad(yF, -ww, yF, -fw, [1, 0], rust, rust);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  geo.setIndex(idx);
  return geo;
}

// SLEEPER SINK (quest-shimmer w53, APP_SURFACE_PERF "quest-shimmer"): a 3D sleeper is a 0.26 m box every
// 0.6 m standing DETAIL.top_m (4 cm) over the ballast — unfiltered geometry. Seen along the track its
// pitch shrinks to a few pixels within tens of metres and the boxes alias into a crawling stripe moiré
// (no MSAA on the Quest's steady profile; the seam lab's station-stand: the structures family = half the
// flicker excess). The ground paints the same sleepers everywhere, band-limited (groundglsl.js
// C_SLEEPERS: boxCov over the texel footprint, octKeep's fade to the mean). So a sleeper instance whose
// along-track PITCH projects under SINK_U.y px (per pass: each eye / the optic / the canvas — the
// viewport from wire.js ribbonUniforms) sinks below the ballast top (by SINK_M at SINK_U.x px and
// less, smoothly up to SINK_U.y) and the painted ones carry the look; nearer it stands as before.
// Kerb boxes (aDet 1) are untouched. Vertex-only: no draw, no fragment cost. SINK_U = [lo, hi] px
// (the seam lab's `sink0` arm: [-2, -1] = never sinks, the look before w53).
export const SINK_U = { value: new THREE.Vector2(3, 6) };
export const SINK_M = 0.2;
function sleeperSink(mat) {
  ribbonUniforms(mat);
  const pitch = WAYSLOOK.ballast.sleeper_pitch_m, sw = WAYSLOOK.ballast.sleeper_w_m;
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uRbView = mat.userData.rbView;
    sh.uniforms.uSink = SINK_U;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aDet;\nuniform vec2 uRbView;\nuniform vec2 uSink;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
#ifdef USE_INSTANCING
  if (aDet < 0.5) {
    mat4 sMV = projectionMatrix * modelViewMatrix * instanceMatrix;
    vec4 sC = sMV * vec4(0.0, 0.0, 0.0, 1.0), sP = sMV * vec4(0.0, 0.0, ${(pitch / sw).toFixed(5)}, 1.0);
    if (sC.w > 1e-3 && sP.w > 1e-3) {
      float sPx = length((sP.xy / sP.w - sC.xy / sC.w) * 0.5 * uRbView);
      transformed.y -= (1.0 - smoothstep(uSink.x, uSink.y, sPx)) * ${SINK_M.toFixed(3)} / max(length(instanceMatrix[1].xyz), 1e-3);
    }
  }
#endif`);
  };
  mat.customProgramCacheKey = () => 'ways-detail-sink';
  return mat;
}

// L-h: the near detail box (sleeper / kerb stone): x across [-0.5, 0.5],
// y [-1, 0] (the top at 0, no bottom face: it is buried), z [-0.5, 0.5]; top
// + four sides, flat normals, 10 triangles
function boxUnit() {
  const pos = [], nrm = [], idx = [];
  const quad = (a, b, c, d, n) => {
    const k = pos.length / 3;
    pos.push(...a, ...b, ...c, ...d);
    for (let i = 0; i < 4; i++) nrm.push(...n);
    idx.push(k, k + 1, k + 2, k, k + 2, k + 3);
  };
  const h = 0.5;
  quad([-h, 0, -h], [-h, 0, h], [h, 0, h], [h, 0, -h], [0, 1, 0]);
  quad([-h, -1, h], [h, -1, h], [h, 0, h], [-h, 0, h], [0, 0, 1]);
  quad([h, -1, -h], [-h, -1, -h], [-h, 0, -h], [h, 0, -h], [0, 0, -1]);
  quad([h, -1, h], [h, -1, -h], [h, 0, -h], [h, 0, h], [1, 0, 0]);
  quad([-h, -1, -h], [-h, -1, h], [-h, 0, h], [-h, 0, -h], [-1, 0, 0]);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  geo.setIndex(idx);
  return geo;
}

export class WaysNet {
  constructor(scene, baseUrl, meta) {
    this.scene = scene;
    // every tile's group and the detail set: hidden until `prepare` built
    // their programs (a late landing would link them inside a frame)
    this.root = new THREE.Group();
    this.root.visible = false;
    scene.add(this.root);
    this.prepare = null;      // (root) => Promise: compile before the first draw (main.js)
    this.graph = null;
    this.trails = [];         // path-family trail index (buildTrailIndex)
    this.trailByRun = new Map();
    this.trailStats = null;
    this.ready = false;
    this.onReady = null;
    // the 2D Map's half (smooth Map): graph + trails as soon as the decode
    // has the graph input — before the 3D stages and meshes (seconds)
    this.mapReady = false;
    this.onMapData = null;
    this.byChunk = new Map(); // "cx_cz" -> THREE.Group (a tile; on quest a wayCellM cell)
    this.cellM = wayCellM();  // the class's upload cell (built after detectDevice)
    this.last = new THREE.Vector3(1e9, 0, 1e9);
    this.pieces = null;
    this.stats = null;
    this.maskP = null;        // -> {at(x, z)} once the pieces are known
    this._maskResolve = null;
    this.maskP = new Promise((res) => { this._maskResolve = res; });
    // settles AFTER `ready = true` (maskP settles before the meshes' prepare is awaited) — resolves to `ready`
    this.readyP = new Promise((res) => { this._readyResolve = res; });
    this._src = [baseUrl, meta];
    this.platforms = null;    // game/pave.js platformsIndex of ways.json `platforms` | null
    this.onPlatforms = null;  // (index) once read (main.js: the heightfield's)
  }

  // main.js starts the ways FIRST in its load phase 1. maskP (and onReady)
  // exist from construction and settle when every tile is in.
  start() {
    const s = this._src;
    if (!s) return this.maskP;
    this._src = null;
    this._load(s[0], s[1]).catch((e) => {
      console.warn('[ways2] ways:', e.message);
      this._maskResolve(null);
      this._readyResolve(false);
    });
    return this.maskP;
  }

  // R0 WAY1: meta.waytiles = the bake's waytiles step — the slim doc
  // (DECODE: client/src/decode.js decodeWays, the graph, trails, carve,
  // platforms) and every way1/ tile, all requested at once in the bulk class
  // (need-ranked: the nearest first; a Beam click pauses it, assetq.js), each
  // uploaded as it lands. A bake without it (older than the step) is refused.
  async _load(baseUrl, meta) {
    const T = meta.waytiles;
    if (!T || T.format !== WAY1.MAGIC || T.version !== WAY1.VERSION || !T.slim || !Array.isArray(T.tiles)) {
      throw new Error(`meta.waytiles ${T ? `${T.format} v${T.version}` : 'missing'}: this client reads ${WAY1.MAGIC} v${WAY1.VERSION} — rebuild this world (Build)`);
    }
    // the slim doc first (the Map's streets and trails hang on it), then the tiles
    const file = T.slim.file;
    const textP = fetchAsset(`${baseUrl}/${file}`, { prio: 'bulk', as: 'text' });
    const M = T.tile_m;
    const got = [];
    let wake = null;
    const land = (i, v) => { got.push([i, v]); if (wake) { wake(); wake = null; } };
    T.tiles.forEach(([tx, tz, bytes], i) => {
      fetchAsset(`${baseUrl}/${WAY1.tileFile(tx, tz)}`, {
        prio: 'bulk', as: 'arrayBuffer',
        need: { rect: [tx * M, tz * M, (tx + 1) * M, (tz + 1) * M], m: null, bytes, keep: true },
      }).then((ab) => land(i, ab), (e) => land(i, e instanceof Error ? e : new Error(String(e))));
    });
    const text = await textP;
    if (text === null) throw new Error(`${file}: HTTP error`);
    const { doc } = await decodeWays(text, file, { onGraph: (d, g) => this._mapData(d, g) });
    for (const [l, f] of this._docSteps(doc)) { await act(l, f); await slice(); }
    act('ways.bores', () => this._bores(doc));
    await slice();
    // the platforms: flat paved areas of the one ground (the pave cut draws
    // them; the heightfields and wayLabelAt read this index)
    act('ways.platforms', () => {
      this.platforms = platformsIndex(doc);
      if (this.platforms && this.onPlatforms) this.onPlatforms(this.platforms);
    });
    await slice();
    const S = T.stats;
    this.paint = S.paint;
    this.forks = S.forks;
    this.decks = S.decks;
    this.marks = S.marks;
    // the ribbon textures (roads3d.js), painted between slices
    act('ways.materials');
    this.mats = await makeMaterials(async () => { await slice(); act('ways.materials'); });
    this.paintMats = {};
    // the tiles in their landing order, one per task; the pieces and the
    // detail records kept per tile (the bake's order: the same concatenation
    // on every load)
    const pcs = new Array(T.tiles.length), det = new Array(T.tiles.length);
    for (let n = 0; n < T.tiles.length; n++) {
      if (!got.length) await new Promise((r) => { wake = r; });
      const [i, ab] = got.shift();
      const name = WAY1.tileFile(T.tiles[i][0], T.tiles[i][1]);
      if (ab === null) throw new Error(`${name}: HTTP error`);
      if (ab instanceof Error) throw new Error(`${name}: ${ab.message}`);
      act('ways.tile', () => {
        const t = WAY1.decodeTile(ab);
        if (t.tx !== T.tiles[i][0] || t.tz !== T.tiles[i][1]) throw new Error(`${name}: carries tile ${t.tx}_${t.tz}`);
        this._upload(t);
        pcs[i] = WAY1.decodePieces(t);
        det[i] = t.detail;
      });
      await slice();
    }
    const pieces = act('ways.pieces', () => pcs.flat().sort((a, b) => a.pid - b.pid));
    pcs.length = 0;
    this.pieces = pieces;
    await slice();
    act('ways.flights', () => this._flightIndex(pieces));
    await slice();
    await runSteps(this._surfIndexSteps(pieces), 'ways.surf');
    await slice();
    act('ways.detail', () => this._detailBuild(det, S.kerb_h_m, S.detail));
    this.stats = { ...S.drawn, ...this._count(), ground: S.ground, paint: this.paint, forks: this.forks, decks: this.decks, marks: this.marks,
      detail: this.detail };
    const prep = this.prepare ? Promise.resolve().then(() => this.prepare(this.root)).catch((e) => console.warn('[ways2] prepare:', e)) : null;
    this._maskResolve(await this._mask(meta, pieces));
    await prep;
    this.root.visible = true;
    this.ready = true;
    this._readyResolve(true);
    if (this.onReady) this.onReady();
  }

  // the graph + the trail index, from decodeWays' onGraph (the doc is
  // read, never written): the Map draws the streets and trails from here.
  // The graph, the trail index and the Map's hook one task each
  async _mapData(doc, gin) {
    // a road's `paved` from its run (furniture: a bench by a tarmac road
    // faces it) — added here, not in graphInput: game/waysdraw.js is a
    // world-stage key input
    for (const r of gin.roads) { const e = doc.ways[r.run]; r.paved = !!(e && e.paved); }
    this.graph = new RoadGraph(gin);
    await slice();
    act('ways.trails');
    this._trails(doc);
    this.mapReady = true;
    await slice();
    act('ways.mapdata');
    if (this.onMapData) {
      try { this.onMapData(); } catch (e) { console.error('[ways2] onMapData:', e); }
    }
  }

  // WAYSG-L3: the ground FLIGHTS as drawn (the stairs runs' ground pieces,
  // game/waysdraw.js isFlightRun: the tile's piece flag, baked), quads on an
  // 8 m grid -> flightAt(x, z): the player walks a ground flight up to its
  // stair limit instead of the open-slope brake (player.js GROUND_FLIGHT_WALK_DEG).
  _flightIndex(pieces) {
    const CELL = 8, grid = new Map(), quads = [];
    for (const p of pieces) {
      if (!p.flight) continue;
      for (let q = 0; q + 1 < p.sec.length; q++) {
        const A = p.sec[q], B = p.sec[q + 1], V = [A.a, A.b, B.b, B.a].map((v) => [v[0], v[2]]);
        const id = quads.length;
        quads.push(V);
        const xs = V.map((v) => v[0]), zs = V.map((v) => v[1]);
        for (let cx = Math.floor(Math.min(...xs) / CELL); cx <= Math.floor(Math.max(...xs) / CELL); cx++) {
          for (let cz = Math.floor(Math.min(...zs) / CELL); cz <= Math.floor(Math.max(...zs) / CELL); cz++) {
            const k = cx + '_' + cz;
            if (!grid.has(k)) grid.set(k, []);
            grid.get(k).push(id);
          }
        }
      }
    }
    this._flights = { CELL, grid, quads };
    this.flightStats = { quads: quads.length };
  }

  // is (x, z) on a drawn ground flight? (even-odd over the cell's quads)
  flightAt(x, z) {
    const F = this._flights;
    if (!F) return false;
    const ids = F.grid.get(Math.floor(x / F.CELL) + '_' + Math.floor(z / F.CELL));
    if (!ids) return false;
    for (const id of ids) {
      const V = F.quads[id];
      let c = false;
      for (let i = 0, j = V.length - 1; i < V.length; j = i++) {
        const [xi, zi] = V[i], [xj, zj] = V[j];
        if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) c = !c;
      }
      if (c) return true;
    }
    return false;
  }

  // the drawn surface under a point (wayLabelAt: the HUD / inspect Surface
  // row): every drawn piece's quads on a 16 m grid (ids only: the quads are
  // the pieces' own sec, which stay resident) and per run its label — the
  // way's own surface tag, else its look kind (game/waysdraw.js wayKind;
  // the tile's piece label, baked: client/src/waybuild.js runLabel).
  // The piece reaches the run's FACADE-W surface (`fw`), so the label does too.
  // _surfIndexSteps = THE index, a step per `every` pieces (M7c: one 95-257
  // ms task); the grid is built aside, `_surf` set in the last step;
  // _surfIndex = its drain
  _surfIndex(pieces) {
    const it = this._surfIndexSteps(pieces);
    for (;;) { const r = it.next(); if (r.done) return r.value; }
  }
  *_surfIndexSteps(pieces, every = 256) {
    const CELL = 16, grid = new Map(), label = new Map();
    for (let pi = 0; pi < pieces.length; pi++) {
      if (pi && pi % every === 0) yield;
      const p = pieces[pi];
      if (p.sec.length < 2) continue;
      if (!label.has(p.run)) label.set(p.run, p.label);
      for (let q = 0; q + 1 < p.sec.length; q++) {
        const A = p.sec[q], B = p.sec[q + 1];
        const x0 = Math.min(A.a[0], A.b[0], B.a[0], B.b[0]), x1 = Math.max(A.a[0], A.b[0], B.a[0], B.b[0]);
        const z0 = Math.min(A.a[2], A.b[2], B.a[2], B.b[2]), z1 = Math.max(A.a[2], A.b[2], B.a[2], B.b[2]);
        for (let cx = Math.floor(x0 / CELL); cx <= Math.floor(x1 / CELL); cx++) {
          for (let cz = Math.floor(z0 / CELL); cz <= Math.floor(z1 / CELL); cz++) {
            const key = cx + '_' + cz;
            let l = grid.get(key);
            if (!l) grid.set(key, (l = []));
            l.push(pi, q);
          }
        }
      }
    }
    this._surf = { CELL, grid, label, pieces };
  }

  // the drawn surface's label at (x, z), or null off every drawn piece: a
  // platforms item first (it claims its ring in the pave cut: a platform
  // "platform · <mat>", a canopy's ground its <mat>) unless a ground flight
  // owns the point (game/pave.js paveAt: flight > platform), then the drawn
  // way's
  wayLabelAt(x, z) {
    const P = this.platforms, p = P ? platformAt(P, x, z) : -1;
    if (p >= 0 && !this.flightAt(x, z)) return platformLabel(P.items[p]);
    const F = this._surf;
    if (!F) return null;
    const l = F.grid.get(Math.floor(x / F.CELL) + '_' + Math.floor(z / F.CELL));
    if (!l) return null;
    for (let i = 0; i < l.length; i += 2) {
      const p = F.pieces[l[i]], A = p.sec[l[i + 1]], B = p.sec[l[i + 1] + 1];
      const V = [A.a, B.a, B.b, A.b];
      let c = false;
      for (let a = 0, b = 3; a < 4; b = a++) {
        const xi = V[a][0], zi = V[a][2], xj = V[b][0], zj = V[b][2];
        if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) c = !c;
      }
      if (c) return F.label.get(p.run) || null;
    }
    return null;
  }

  // the trail index (before the doc is dropped). Never a throw. Each entry keeps
  // the run's OSM `way` (MAP-D: main.js joins meta.pathinfo / meta.routes on
  // it — joinTrailData — before the map layer and the marks are built)
  _trails(doc) {
    try {
      const { trails, byRun, stats } = buildTrailIndex(doc);
      this.trails = trails; this.trailByRun = byRun; this.trailStats = stats;
    } catch (e) {
      console.warn('[trails] trail index unavailable:', e.message);
      this.trails = []; this.trailByRun = new Map(); this.trailStats = null;
    }
  }

  // nearest path-family trail (index entry) within r m of (x, y, z), or null
  trailAt(x, y, z, r = 8, dy = 6) {
    return trailNear(this.graph, this.trailByRun, x, y, z, r, dy);
  }

  // a subclass's own reads of the doc ([label, fn], one task each — or, an
  // async fn, its own slices — before the carve: main.js WaysDrawNet, the
  // structure rails and the deck cover)
  _docSteps(doc) { return []; }

  // the bore / site / deck carve (S6.a / S6.b) registered with corridor.js:
  // boreruns.js, its own module so the ground-tree cut keys without this file
  _bores(doc) { Object.assign(this, addBoreRuns(doc)); }

  // one WAY1 tile (game/way1.js decodeTile) -> its chunk group: each mesh's
  // attributes and index as the bake packed them (the BufferGeometry the
  // ribbon pass used to build here, client/src/waybuild.js packAcc), each
  // rail group ONE instanced railUnit draw (the head segments, then the frogs)
  _upload(t) {
    const C = this.cellM, merge = C !== CHUNK;
    const cx = Math.floor((t.tx + 0.5) * CHUNK / C), cz = Math.floor((t.tz + 0.5) * CHUNK / C);
    const k = merge ? `c${cx}_${cz}` : WAY1.tileKey(t.tx, t.tz);
    let g = this.byChunk.get(k);
    if (!g) {
      g = new THREE.Group();
      g.userData.cx = (cx + 0.5) * C; g.userData.cz = (cz + 0.5) * C;   // = the tile's centre when C = CHUNK
      this.byChunk.set(k, g);
      this.root.add(g);
    }
    for (const m of t.meshes) {
      const geo = new THREE.BufferGeometry();
      for (const a of m.attrs) geo.setAttribute(a.name, new THREE.BufferAttribute(a.array, a.size, !!a.norm));
      geo.setIndex(new THREE.BufferAttribute(m.index, 1));
      geo.computeBoundingSphere();
      // a deck way (deckway.js): material 'deck', the ground's look
      const mat = m.mat === 'deck' ? (this._dwMat || (this._dwMat = deckWayMaterial()))
        : m.painted ? this._paintMat(m.mat) : this.mats[m.mat];
      if (merge) {
        // the cell's mesh of this (material, view class) grows by the tile's (quest-merge)
        const prev = g.children.find((o) => !o.isInstancedMesh && o.material === mat && o.userData.cls === m.view);
        const merged = prev && mergeGeometries(prev.geometry, geo);
        if (merged) { prev.geometry.dispose(); geo.dispose(); prev.geometry = merged; continue; }
      }
      const mesh = new THREE.Mesh(geo, mat);
      mesh.userData.cls = m.view;
      mesh.matrixAutoUpdate = false;
      g.add(mesh);
    }
    for (const r of t.rails) {
      const prev = merge ? g.children.find((o) => o.isInstancedMesh && o.userData.cls === r.view) : null;
      const n0 = prev ? prev.count : 0, n = n0 + r.matrices.length / 16;
      const im = new THREE.InstancedMesh(this._railUnit || (this._railUnit = railUnit()), this._railMat(), n);
      if (prev) { im.instanceMatrix.array.set(prev.instanceMatrix.array.subarray(0, n0 * 16)); g.remove(prev); prev.dispose(); }
      im.instanceMatrix.array.set(r.matrices, n0 * 16);
      im.instanceMatrix.needsUpdate = true;
      im.computeBoundingSphere();
      im.userData.cls = r.view;
      im.matrixAutoUpdate = false;
      g.add(im);
    }
  }

  // the PAINT twin of a ribbon class material: the map lookup split by the
  // section's u bounds (see the module header); one program per class
  _paintMat(name) {
    if (this.paintMats[name]) return this.paintMats[name];
    const base = this.mats[name];
    const m = base.clone();
    const C = PAINT.colour, col = (h) => new THREE.Color(h);
    const uni = {
      uPaintA: { value: new THREE.Vector4(PAINT.slab_m, PAINT.joint_m, PAINT.kerb_line_m, PAINT.sett_m) },
      uPaintB: { value: new THREE.Vector4(MARKS.line_m, 0, 0, 0) },
      uPaintC: { value: new THREE.Vector4(0, 0, PAINT.w_unit_m, PAINT.sett_joint_m / PAINT.sett_m) },
      uMarkA: { value: new THREE.Vector4(MARKS.centre_dash_m, MARKS.centre_gap_m, MARKS.lane_dash_m, MARKS.lane_gap_m) },
      uPaintSidewalk: { value: col(C.sidewalk) }, uPaintJoint: { value: col(C.joint) }, uPaintKerb: { value: col(C.kerb) },
      uPaintApron: { value: col(C.apron) }, uPaintSett: { value: col(C.sett) }, uPaintLine: { value: col(C.line) },
    };
    m.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, uni);
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nattribute vec4 aPart;\nattribute vec4 aWay;\nattribute vec2 aLane;\nattribute vec2 aCut;\n'
          + 'varying vec4 vPart;\nvarying vec4 vWay;\nvarying vec2 vLane;\nflat varying vec2 vCut;\nuniform vec4 uPaintC;')
        .replace('#include <uv_vertex>', '#include <uv_vertex>\nvPart = aPart;\nvWay = vec4(aWay.x * uPaintC.z, aWay.yzw);\nvLane = aLane;\nvCut = aCut;');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec4 vPart;\nvarying vec4 vWay;\nvarying vec2 vLane;\nflat varying vec2 vCut;\nuniform vec4 uMarkA;\n'
          + 'uniform vec4 uPaintA;\nuniform vec4 uPaintB;\nuniform vec4 uPaintC;\nuniform vec3 uPaintSidewalk;\nuniform vec3 uPaintJoint;\n'
          + 'uniform vec3 uPaintKerb;\nuniform vec3 uPaintApron;\nuniform vec3 uPaintSett;\nuniform vec3 uPaintLine;')
        .replace('#include <map_fragment>', PAINT_GLSL);
    };
    m.customProgramCacheKey = () => 'ways2paint-' + name;
    this.paintMats[name] = m;
    return m;
  }

  _count() {
    let meshes = 0, tris = 0, instances = 0;
    for (const g of this.byChunk.values()) {
      for (const m of g.children) {
        meshes++;
        const ix = m.geometry.index;
        const t = (ix ? ix.count : m.geometry.attributes.position.count) / 3;
        if (m.isInstancedMesh) { tris += t * m.count; instances += m.count; } else tris += t;
      }
    }
    return { chunks: this.byChunk.size, meshes, tris: Math.round(tris), rail_instances: instances };
  }

  // the rail steel: vertex colours (railUnit) on a white Lambert
  _railMat() {
    if (!this._rmat) this._rmat = new THREE.MeshLambertMaterial({ color: 0xffffff, vertexColors: true, fog: true });
    this._rmat.name = 'ways-rail';
    return this._rmat;
  }

  // L-h near detail (waysdraw nearDetail, the bake's: the tiles' records,
  // sleepers [x, y, z, ax, az, len, run] — the drawn pieces' then (BRIDGE P1)
  // the rail deck ways' — and kerb faces [P0, P1, s, w, -], f32): every
  // sleeper / kerb stone as an instance matrix + colour, indexed on a 64 m
  // grid; ONE InstancedMesh (cap renderconfig ways.detail_cap) shows those
  // within sleeper_view_m / kerb_view_m of the player, re-selected every
  // detail_step_m (_detailPick). det: per tile {sleepers, kerbs}, h_m the
  // kerb height, stats the bake's
  _detailBuild(det, h_m, stats) {
    const cat = (key, w) => {
      const out = new Float32Array(det.reduce((s, d) => s + d[key].length, 0));
      let o = 0;
      for (const d of det) { out.set(d[key], o); o += d[key].length; }
      return out.length % w ? null : out;
    };
    const D = { sleepers: cat('sleepers', 7), kerbs: cat('kerbs', 10), h_m };
    if (!D.sleepers || !D.kerbs) throw new Error('way1 detail records: a partial record');
    const nS = D.sleepers.length / 7, nK = D.kerbs.length / 10, n = nS + nK;
    this.detail = { ...stats, cap: WCFG.detail_cap, shown: 0, over_cap: 0 };
    if (!n) return;
    const M = new Float32Array(16 * n), C = new Float32Array(3 * n), type = new Uint8Array(n), X = new Float32Array(2 * n);
    const depth = DETAIL.depth_m, sw = WAYSLOOK.ballast.sleeper_w_m;
    for (let i = 0; i < nS; i++) {
      const o = 7 * i, x = D.sleepers[o], y = D.sleepers[o + 1], z = D.sleepers[o + 2], ax = D.sleepers[o + 3], az = D.sleepers[o + 4], len = D.sleepers[o + 5];
      // x across (len), y up (depth), z along (width); a hair per run parity
      // so two runs' overlapping sleepers in a turnout never z-fight
      const yy = y + 0.003 * (D.sleepers[o + 6] & 1);
      M.set([ax * len, 0, az * len, 0, 0, depth, 0, 0, -az * sw, 0, ax * sw, 0, x, yy, z, 1], 16 * i);
      const j = jit(x, z, 0.16);
      C.set([SLEEPER_C.r * j, SLEEPER_C.g * j, SLEEPER_C.b * j], 3 * i);
      X[2 * i] = x; X[2 * i + 1] = z;
    }
    const h = D.h_m, below = DETAIL.kerb_below_m;
    for (let k = 0; k < nK; k++) {
      const o = 10 * k, i = nS + k, K = D.kerbs;
      const x0 = K[o], y0 = K[o + 1], z0 = K[o + 2], x1 = K[o + 3], y1 = K[o + 4], z1 = K[o + 5], sx = K[o + 6], sz = K[o + 7], w = K[o + 8];
      // x along the face (P0 -> P1, the slope in), y the face (h over the
      // edge + below), z the stone width toward the sidewalk, centred there
      const cx = (x0 + x1) / 2 + sx * w / 2, cz = (z0 + z1) / 2 + sz * w / 2, cy = (y0 + y1) / 2 + h;
      // the box is symmetric across: z = -s on a left-handed basis (a
      // mirrored instance turns the box inside out — three culls per mesh,
      // not per instance: the kerb drew its back faces, the joints as cubes)
      const hs = (x1 - x0) * sz - (z1 - z0) * sx >= 0 ? 1 : -1;
      M.set([x1 - x0, y1 - y0, z1 - z0, 0, 0, h + below, 0, 0, hs * sx * w, 0, hs * sz * w, 0, cx, cy, cz, 1], 16 * i);
      const j = jit(cx, cz, 0.1);
      C.set([KERB_C.r * j, KERB_C.g * j, KERB_C.b * j], 3 * i);
      X[2 * i] = cx; X[2 * i + 1] = cz;
      type[i] = 1;
    }
    const G = 64, cells = new Map();
    for (let i = 0; i < n; i++) {
      const key = Math.floor(X[2 * i] / G) + '_' + Math.floor(X[2 * i + 1] / G);
      let L = cells.get(key);
      if (!L) cells.set(key, L = []);
      L.push(i);
    }
    const cap = Math.min(WCFG.detail_cap, n);
    const geo = this._boxUnit || (this._boxUnit = boxUnit());
    // the instance's kind (0 sleeper, 1 kerb) for the sleeper sink (sleeperSink)
    geo.setAttribute('aDet', new THREE.InstancedBufferAttribute(new Float32Array(cap), 1));
    const mesh = new THREE.InstancedMesh(geo, sleeperSink(new THREE.MeshLambertMaterial({ color: 0xffffff, fog: true })), cap);
    mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(3 * cap), 3);
    mesh.count = 0;
    mesh.frustumCulled = false;      // the set moves with the player
    mesh.matrixAutoUpdate = false;
    mesh.userData.detail = true;
    this.root.add(mesh);
    this._det = { M, C, type, X, cells, G, mesh, cap, at: new THREE.Vector3(1e9, 0, 1e9) };
  }

  // the instances within their type's range of pos, nearest cells first
  // until the cap (over_cap counts what the cap left out)
  _detailPick(pos) {
    const d = this._det;
    if (!d || pos.distanceTo(d.at) < WCFG.detail_step_m) return;
    d.at.copy(pos);
    const R = [WCFG.sleeper_view_m, WCFG.kerb_view_m], Rm = Math.max(R[0], R[1]), G = d.G;
    const i0 = Math.floor((pos.x - Rm) / G), i1 = Math.floor((pos.x + Rm) / G);
    const j0 = Math.floor((pos.z - Rm) / G), j1 = Math.floor((pos.z + Rm) / G);
    const want = [];
    for (let i = i0; i <= i1; i++) for (let j = j0; j <= j1; j++) {
      const L = d.cells.get(i + '_' + j);
      if (!L) continue;
      const cd = Math.hypot((i + 0.5) * G - pos.x, (j + 0.5) * G - pos.z);
      if (cd - G * Math.SQRT1_2 > Rm) continue;
      want.push([cd, L]);
    }
    want.sort((a, b) => a[0] - b[0]);
    const MI = d.mesh.instanceMatrix.array, CI = d.mesh.instanceColor.array, KA = d.mesh.geometry.attributes.aDet;
    let n = 0, over = 0;
    for (const [, L] of want) for (const i of L) {
      const r = R[d.type[i]], dx = d.X[2 * i] - pos.x, dz = d.X[2 * i + 1] - pos.z;
      if (dx * dx + dz * dz > r * r) continue;
      if (n >= d.cap) { over++; continue; }
      MI.set(d.M.subarray(16 * i, 16 * i + 16), 16 * n);
      CI.set(d.C.subarray(3 * i, 3 * i + 3), 3 * n);
      KA.array[n] = d.type[i];
      n++;
    }
    d.mesh.count = n;
    d.mesh.instanceMatrix.needsUpdate = true;
    d.mesh.instanceColor.needsUpdate = true;
    KA.needsUpdate = true;
    this.detail.shown = n;
    this.detail.over_cap = over;
  }

  // every piece rastered at 2 m/px (row 0 = z 0) -> {at(x, z)} (grass /
  // trees / shrubs / bike / net: a way is still a way, drawn by the ground or
  // not). The coverage is filled WITHOUT a canvas (maskfill.js: the
  // thresholds 0.3 / 0.35 read it — a canvas read-back is farbled in Brave);
  // only the one Uint8 plane outlives the call; sliced (sched.js: ~0.25 s of
  // fill on a 14 x 8 km world). WAYS-G: no drawn-strip plane / terrain
  // texture any more.
  // EXACT-DATA-BEGIN (scripts/check-ring.mjs exact:no-canvas: no canvas, bitmap or readback in here)
  async _mask(meta, pieces) {
    try {
      const g0 = meta.grid;
      const w = Math.max(1, Math.round((g0.W * g0.cell_m) / MASK_CELL));
      const h = Math.max(1, Math.round((g0.H * g0.cell_m) / MASK_CELL));
      const { mask: all, fill } = maskFill(w, h);
      const s = 1 / MASK_CELL;
      const poly = [];
      for (const p of pieces) {
        const S = p.sec;
        poly.length = 0;
        for (let i = 0; i < S.length; i++) poly.push(S[i].a[0] * s, S[i].a[2] * s);
        for (let i = S.length - 1; i >= 0; i--) poly.push(S[i].b[0] * s, S[i].b[2] * s);
        fill(poly);
        await slice();
      }
      return {
        at(x, z) {
          const j = Math.min(w - 1, Math.max(0, Math.floor(x / MASK_CELL)));
          const i = Math.min(h - 1, Math.max(0, Math.floor(z / MASK_CELL)));
          return all[i * w + j] / 255;
        },
      };
    } catch (e) {
      console.warn('[ways2] road mask:', e.message);
      return null;
    }
  }
  // EXACT-DATA-END

  update(pos) {
    if (!this.ready) return;
    this._detailPick(pos);
    if (pos.distanceTo(this.last) < 64) return;
    this.last.copy(pos);
    for (const g of this.byChunk.values()) {
      const d = Math.hypot(g.userData.cx - pos.x, g.userData.cz - pos.z);
      let any = false;
      for (const m of g.children) { m.visible = ribbonVisible(m.userData.cls, d); any = any || m.visible; }
      g.visible = any;
    }
  }
}
